import React, { useState, useEffect, useContext, Fragment } from "react";
import { InputContext } from "../shared/contexts/InputContext";
import { OutputContext } from "../shared/contexts/OutputContext";
import { fetchData } from "../shared/apis/api";
import ResultsHeader from "../components/results/header/ResultsHeader";
import DateIncrementer from "../components/results/inputs/DateIncrementer";
import PensionBarChart from "../components/results/charts/PensionBarChart";
import LumpSumBarChart from "../components/results/charts/LumpSumBarChart";
import RadioInput from "../components/general/inputs/RadioInput";
import SliderInput from "../components/general/inputs/SliderInput";
import AbatementWarning from "../components/results/AbatementWarning";
import classes from "./3-Results.module.css";
import DataFrameTable from "../shared/utils/DataFrameTable";
import CheckingInfoBox from "../shared/utils/CheckingInfoBox";
import ResultsPage1 from "../assests/videos/7_Results_Page_1.mp4";
import ResultsPage2 from "../assests/videos/7_Results_Page_2.mp4";
import VideoButton from "../components/general/VideoButton";

const ResultsPage = () => {
  const [error, setError] = useState(null);
  const { inputs, updateInput, errors } = useContext(InputContext);
  const { outputs, updateOutputs, loading, setLoading } =
    useContext(OutputContext);

  const [checkingData, setCheckingData] = useState(null);
  useEffect(() => {
    const fetchDataFromBackend = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchData(inputs);
        // console.log(data)
        //There are two elements of the `data` array that only exist for checking purposes.
        //`dataToUpdate should not contain these; it is used to populate the user-facing charts and tables.
        const dataToUpdate = data.filter(
          (x) =>
            x["benefitType"] != "main_df_check" &&
            x["benefitType"] != "commutation_check"
        );
        //get the data that only exists for checking purposes and use it to set the value of checkingData
        setCheckingData({
          main_df: data.filter((x) => x["benefitType"] == "main_df_check")[0][
            "main_df"
          ],
          checking_info: data.filter(
            (x) => x["benefitType"] == "commutation_check"
          ), //this might be an empty array, so don't track to unpack it yet
        });
        updateOutputs(dataToUpdate);
      } catch (error) {
        setError("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDataFromBackend();
  }, [inputs]);

  let debounceTimeout;

  const handleSettingChange = (group, name, value) => {
    clearTimeout(debounceTimeout);

    debounceTimeout = setTimeout(() => {
      updateInput(group, name, value);
    }, 425);
  };

  //console.log(checkingData)

  return (
    <div>
      <VideoButton videoTitle="Results Page" video={ResultsPage1} />
      <h2>Results</h2>
      <p>
        The numbers below illustrate the annual pension and lump sum amounts
        that you may receive at partial and full retirement. You can update the
        dates of these retirement events by using the controls provided.
      </p>
      <AbatementWarning />
      <div className={classes.date_container}>
        <DateIncrementer
          inputs={inputs}
          updateInput={handleSettingChange}
          label={"Full Retirement Date"}
        />
      </div>
      <div className={classes.date_container}>
        <RadioInput
          title={"Adjust for Inflation"}
          desc_text={
            "You can use these buttons to choose whether or not the illustration results should be in real or nominal values."
          }
          help_text={
            "You can choose to display your pension and lump sum in real or nominal terms. Nominal terms will show the value of lump sum and pension in terms of monetary amounts at the point of payment. Real terms is the nominal value adjusted for inflation and therefore will represent value in current money terms."
          }
          options={[
            { label: "Real", value: "real" },
            { label: "Nominal", value: "nominal" },
          ]}
          inputObject={"projectionSettings"}
          fieldName={"realNominalSwitch"}
          inputs={inputs}
          updateInput={updateInput}
          errors
        />
        {inputs.memberInfo.legacyScheme === "classic" && (
          <RadioInput
            title={"Equalise Lump Sums"}
            help_text={
              "Where the 'Equal' toggle is selected, it is assumed that the standard automatic lump sum is taken in the legacy scheme scenario and that the commutation amount taken in the alpha scheme scenario is such that the total lump sums are equal in both scenarios."
            }
            desc_text={
              "You can use these buttons to set additional commutation to zero or set lump sums to be equal in both scenarios."
            }
            options={[
              { label: "Unequal", value: "unequal" },
              { label: "Equal", value: "equal" },
            ]}
            inputObject={"projectionSettings"}
            fieldName={"equaliseLumpSumsSwitch"}
            inputs={inputs}
            updateInput={updateInput}
            errors={errors}
          />
        )}
        <SliderInput
          title={"Salary Increases in Excess of Inflation"}
          question={
            "This slider can set the annual salary increases (in excess of inflation) you will receive until leaving the scheme. Your pension is directly linked to salary increases."
          }
          help_text={
            <>
              <p>
                You can set your own future salary increases between -2% and 3% p.a.
                in excess of inflation. For future salary increases, in nominal
                terms, this would be 0% and 5% p.a. (i.e. after allowing for 2%
                assumed future CPI inflation).{" "}
              </p>
              <p>
                If you do not enter a salary as at 1 April 2015 on the “Your
                Details” page, salaries between April 2015 and the current date
                are estimated using high level historical salary increase assumptions.{" "}
              </p>
            </>
          }
          inputObject={"projectionSettings"}
          fieldName={"salaryIncreases"}
          inputs={inputs}
          updateInput={handleSettingChange}
          min={-2}
          max={3}
          step={0.1}
        />
        <SliderInput
          title={"Proportion of Legacy Pension Claimed at Partial Retirement"}
          question={
            "This slider can set the proportion of legacy pension that you will receive at partial retirement."
          }
          inputObject={"projectionSettings"}
          fieldName={"legacyProportionClaimedPartRet"}
          inputs={inputs}
          updateInput={handleSettingChange}
          min={0}
          max={100}
          step={1}
        />
        <SliderInput
          title={"Proportion of alpha Pension Claimed at Partial Retirement"}
          question={
            "This slider can set the proportion of alpha pension that you will receive at partial retirement."
          }
          inputObject={"projectionSettings"}
          fieldName={"reformProportionClaimedPartRet"}
          inputs={inputs}
          updateInput={handleSettingChange}
          min={0}
          max={100}
          step={1}
        />
      </div>
      <VideoButton videoTitle="Results Page" video={ResultsPage2} />
      <p>
        Please note that projected figures below are total benefits at partial
        and full retirement (i.e. include non-Remedy Period benefits and Remedy
        Period benefits). They are before any tax adjustments that may apply.
      </p>
      <ResultsHeader />
      <div className={classes.graph_container}>
        <div className={classes.chart_container}>
          <PensionBarChart retirementType={"partial"} />
        </div>
        <div className={classes.chart_container}>
          <PensionBarChart retirementType={"full"} />
        </div>
        <div className={classes.chart_container}>
          <LumpSumBarChart retirementType={"partial"} />
        </div>
        <div className={classes.chart_container}>
          <LumpSumBarChart retirementType={"full"} />
        </div>
      </div>

      {inputs.projectionSettings.equaliseLumpSumsSwitch === "unequal" && (
        <div className={classes.date_container}>
          <SliderInput
            title={
              "Proportion of maximum allowed Legacy Pension commuted at Partial Retirement"
            }
            inputObject={"projectionSettings"}
            fieldName={"legacyLumpSumPartRet"}
            inputs={inputs}
            updateInput={handleSettingChange}
            min={0}
            max={100}
            step={1}
          />
          <SliderInput
            title={"Proportion of maximum allowed alpha Pension commuted at Partial Retirement"}
            inputObject={"projectionSettings"}
            fieldName={"reformLumpSumPartRet"}
            inputs={inputs}
            updateInput={handleSettingChange}
            min={0}
            max={100}
            step={1}
          />
          <SliderInput
            title={"Proportion of maximum allowed Legacy Pension commuted at Full Retirement"}
            inputObject={"projectionSettings"}
            fieldName={"legacyLumpSumFullRet"}
            inputs={inputs}
            updateInput={handleSettingChange}
            min={0}
            max={100}
            step={1}
          />
          <SliderInput
            title={"Proportion of maximum allowed alpha Pension commuted at Full Retirement"}
            inputObject={"projectionSettings"}
            fieldName={"reformLumpSumFullRet"}
            inputs={inputs}
            updateInput={handleSettingChange}
            min={0}
            max={100}
            step={1}
          />
        </div>
      )}
      <p>
        As noted above, the figures shown in these results are before any tax
        adjustments that may apply. The user should be aware that lump sums
        arising from commutation above the tax free allowance will be subject to
        tax.
      </p>
      {window.show_check && (
        <Fragment>
          <h3>Inputs</h3>
          <pre>{JSON.stringify(inputs, null, 2)}</pre>
          {loading ? (
            <p>LOADING...</p>
          ) : error ? (
            <p>ERROR!!!</p>
          ) : (
            <>
              <h3>Outputs</h3>
              <pre>{JSON.stringify(outputs, null, 2)}</pre>
            </>
          )}
        </Fragment>
      )}
      {window.show_commutation_check &&
        checkingData &&
        checkingData["checking_info"][0] && (
          <CheckingInfoBox
            data={checkingData["checking_info"][0]["checking_info"]}
          />
        )}
      {window.show_check && checkingData && (
        <DataFrameTable data={checkingData["main_df"]} />
      )}
    </div>
  );
};

export default ResultsPage;
